/**
 * Lettura tollerante di commerce_stores.sync_state (10 sez. 9.1, 16 sez. 2.16). Modulo puro.
 * Lo scrive solo il worker del core, a intervalli (al massimo ogni 3 s per negozio): barre e contatori
 * dal vivo leggono da qui, il dettaglio (elenchi, errori) si rilegge a richiesta.
 * Chiavi: job (runner), pause, last_error, metrics (metrics-flush, campi camelCase), updated_at.
 */
import type { PauseReason } from './types';

export interface JobProgress {
  id: string | null;
  kind: string | null;
  phase: string | null;
  done: number;
  total: number | null;
  errors: number;
  skipped: number;
  startedAt: string | null;
  finished: boolean;
}

export interface PauseInfo {
  reason: PauseReason;
  since: string | null;
  by: string | null;
}

export interface LastError {
  at: string | null;
  code: string | null;
  message: string | null;
}

export interface OutboxCounts {
  pending: number;
  error: number;
  dead: number;
  held: number;
}

export interface StoreMetrics {
  todayOrders: number;
  openConflicts: number;
  openDecisions: number;
  outbox: OutboxCounts;
  lastWebhookAt: string | null;
  computedAt: string | null;
}

export interface SyncStateView {
  job: JobProgress | null;
  pause: PauseInfo | null;
  lastError: LastError | null;
  metrics: StoreMetrics | null;
  updatedAt: string | null;
}

const PAUSE_REASONS: readonly PauseReason[] = ['manual', 'store_down', 'circuit', 'billing', 'global'];

const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const countOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;

function parseJob(raw: unknown): JobProgress | null {
  const j = obj(raw);
  if (!j) return null;
  const phase = str(j.phase);
  return {
    id: str(j.id),
    kind: str(j.kind),
    phase,
    done: count(j.done),
    total: countOrNull(j.total),
    errors: count(j.errors),
    skipped: count(j.skipped),
    startedAt: str(j.started_at),
    finished: phase === 'done',
  };
}

function parsePause(raw: unknown): PauseInfo | null {
  const p = obj(raw);
  const reason = p ? str(p.reason) : null;
  if (!p || !reason || !(PAUSE_REASONS as readonly string[]).includes(reason)) return null;
  return { reason: reason as PauseReason, since: str(p.since), by: str(p.by) };
}

function parseLastError(raw: unknown): LastError | null {
  const e = obj(raw);
  if (!e) return null;
  return { at: str(e.at), code: str(e.code), message: str(e.message) };
}

function parseMetrics(raw: unknown): StoreMetrics | null {
  const m = obj(raw);
  if (!m) return null;
  const o = obj(m.outbox) ?? {};
  return {
    todayOrders: count(m.todayOrders),
    openConflicts: count(m.openConflicts),
    openDecisions: count(m.openDecisions),
    outbox: { pending: count(o.pending), error: count(o.error), dead: count(o.dead), held: count(o.held) },
    lastWebhookAt: str(m.lastWebhookAt),
    computedAt: str(m.computed_at),
  };
}

export function parseSyncState(raw: unknown): SyncStateView {
  const s = obj(raw) ?? {};
  return {
    job: parseJob(s.job),
    pause: parsePause(s.pause),
    lastError: parseLastError(s.last_error),
    metrics: parseMetrics(s.metrics),
    updatedAt: str(s.updated_at),
  };
}

/** Percentuale del lavoro (0-100), null se il totale non e' noto. */
export function jobPercent(j: { done: number; total: number | null }): number | null {
  if (j.total === null || j.total <= 0) return null;
  return Math.max(0, Math.min(100, Math.floor((j.done / j.total) * 100)));
}

export interface StoreProblems {
  /** Elementi non riusciti dopo i tentativi o in errore (outbox error + dead). */
  errors: number;
  /** Operazioni ferme per un freno. */
  held: number;
  conflicts: number;
  /** Abbinamenti incerti da decidere. */
  decisions: number;
  /** In coda: non sono errori, il motore riprova da solo. */
  queued: number;
  /** "Da guardare": errori + conflitti + freni (08 sez. 7.1). */
  total: number;
}

export function storeProblems(s: SyncStateView): StoreProblems {
  const m = s.metrics;
  const errors = m ? m.outbox.error + m.outbox.dead : 0;
  const heldOps = m ? m.outbox.held : 0;
  const held = heldOps > 0 ? heldOps : s.job?.phase === 'held' ? 1 : 0;
  const conflicts = m ? m.openConflicts : 0;
  return {
    errors,
    held,
    conflicts,
    decisions: m ? m.openDecisions : 0,
    queued: m ? m.outbox.pending : 0,
    total: errors + conflicts + held,
  };
}

/**
 * Tempo stimato che manca, dal ritmo tenuto finora (null se non stimabile: niente totale, appena
 * partito, meno di 1% fatto). Serve solo come indicazione ("circa 4 minuti").
 */
export function estimateRemainingMs(j: { done: number; total: number | null; startedAt: string | null }, now: number = Date.now()): number | null {
  if (j.total === null || j.total <= 0 || j.done <= 0 || !j.startedAt) return null;
  const started = Date.parse(j.startedAt);
  if (!Number.isFinite(started) || now <= started) return null;
  if (j.done / j.total < 0.01 || j.done >= j.total) return null;
  const perItem = (now - started) / j.done;
  return Math.round(perItem * (j.total - j.done));
}
